import type { IdsReport, IdsSpecification } from '@manudev/model';

export type Verdict = 'pass' | 'fail';

export interface ElementFailure {
  globalId: string;
  name: string;
  requirements: string[];
  reasons: string[];
}

export interface SpecResult {
  spec: string;
  verdict: Verdict;
  requirements: string[];
}

// An element fails the rule set when it fails any specification that applies to it.
export function verdicts(report: IdsReport): Map<string, Verdict> {
  const out = new Map<string, Verdict>();
  for (const spec of report.specifications) {
    for (const id of spec.passedIds) if (!out.has(id)) out.set(id, 'pass');
    for (const f of spec.failed) out.set(f.globalId, 'fail');
  }
  return out;
}

// the failure rows of one specification, one per element
export function failuresOf(spec: IdsSpecification): ElementFailure[] {
  const byId = new Map<string, ElementFailure>();
  for (const f of spec.failed) {
    let row = byId.get(f.globalId);
    if (!row) {
      row = { globalId: f.globalId, name: f.name, requirements: [], reasons: [] };
      byId.set(f.globalId, row);
    }
    row.requirements.push(tidy(f.requirement));
    row.reasons.push(f.reason);
  }
  return [...byId.values()];
}

export function resultsFor(report: IdsReport, globalId: string): SpecResult[] {
  const out: SpecResult[] = [];
  for (const spec of report.specifications) {
    const failed = spec.failed.filter((f) => f.globalId === globalId);
    if (failed.length)
      out.push({
        spec: spec.name,
        verdict: 'fail',
        requirements: failed.map((f) => tidy(f.requirement)),
      });
    else if (spec.passedIds.includes(globalId))
      out.push({ spec: spec.name, verdict: 'pass', requirements: [] });
  }
  return out;
}

// the translator quotes names it already got quoted; the list of what is there is noise here
export function tidy(text: string): string {
  return text.replace(/""/g, '"').replace(/\.\s*Available:.*$/s, '');
}
